<template>
    <div v-if="isOpen" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
        <div :class="$slots.leftPanel ? 'w-[60vw]' : 'w-[45vw]'" class="bg-white rounded-lg h-[90vh] flex flex-col shadow-2xl">
            <div class="flex items-center justify-between p-6 border-b border-gray-200">
                <div>
                    <h3 class="text-lg font-semibold">{{ modalTitle }}</h3>
                </div>
                
                <!-- Action buttons in header -->
                <div class="flex items-center space-x-3">
                    <button 
                        v-if="showDownload"
                        @click="downloadReport"
                        class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center space-x-2 text-sm"
                        title="Download PDF"
                    >
                        <svg-icon type="mdi" :path="icons.downloadReport" :size="16"></svg-icon>
                        <span>Download</span>
                    </button>
                    
                    <button 
                        v-if="showEmail"
                        @click="$emit('email-report')"
                        class="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center space-x-2 text-sm"
                        title="Email Report"
                    >
                        <svg-icon type="mdi" :path="icons.emailReport" :size="16"></svg-icon>
                        <span>Email</span>
                    </button>
                    
                    <button 
                        v-if="showFtpUpload"
                        @click="$emit('ftp-upload')"
                        class="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 flex items-center space-x-2 text-sm"
                        title="Upload to Dairygold FTP"
                    >
                        <svg-icon type="mdi" :path="icons.ftpUpload" :size="16"></svg-icon>
                        <span>FTP</span>
                    </button>
                    
                    <button @click="closeModal" class="text-gray-400 hover:text-gray-600 ml-4">
                        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                        </svg>
                    </button>
                </div>
            </div>
            
            <!-- Conditional Layout: With or Without Left Panel -->
            <div class="flex flex-1 overflow-hidden">
                <!-- Left Panel: Only shown when slot content exists -->
                <div v-if="$slots.leftPanel" class="w-2/6 p-6 border-r border-gray-200 overflow-y-auto">
                    <slot name="leftPanel"></slot>
                </div>
                
                <!-- Right Panel: PDF Preview (full-width when no left panel) -->
                <div :class="$slots.leftPanel ? 'w-4/6' : 'w-full'" class="bg-gray-50 flex flex-col">
                    <div class="flex-1 p-4">
                        <div v-if="pdfUrl" class="h-full">
                            <iframe 
                                :src="pdfUrl" 
                                class="w-full h-full border border-gray-300 rounded"
                                :title="`${reportTypeLabel} Preview`"
                            ></iframe>
                        </div>
                        <div v-else class="h-full flex items-center justify-center text-gray-500">
                            <div class="text-center">
                                <svg class="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                                </svg>
                                <p class="text-sm">{{ noPreviewMessage || 'Report preview not available' }}</p>
                                <p class="text-xs text-gray-400">{{ noPreviewSubMessage || 'Generate the report first to see preview' }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import * as icons from '../Partials/icons.js';

export default {
    emits: ['close', 'download', 'email-report', 'ftp-upload'],
    props: {
        isOpen: {
            type: Boolean,
            default: false
        },
        modalTitle: {
            type: String,
            default: 'Report Preview'
        },
        reportType: {
            type: String,
            default: 'soil_analysis'
        },
        pdfUrl: {
            type: String,
            default: null
        },
        downloadUrl: {
            type: String,
            default: null
        },
        showDownload: {
            type: Boolean,
            default: true
        },
        showEmail: {
            type: Boolean,
            default: false
        },
        showFtpUpload: {
            type: Boolean,
            default: false
        },
        noPreviewMessage: {
            type: String,
            default: null
        },
        noPreviewSubMessage: {
            type: String,
            default: null
        }
    },
    computed: {
        icons() {
            return icons;
        },
        reportTypeLabel() {
            const labels = {
                'soil_analysis': 'Soil Analysis Report',
                'soil-analysis': 'Soil Analysis Report',
                'task_report': 'Task Report',
                'combined_soil_analysis': 'Combined Soil Analysis Report',
                'combined_task_report': 'Combined Task Report'
            };
            return labels[this.reportType] || 'Report';
        }
    },
    methods: {
        closeModal() {
            this.$emit('close');
        },
        downloadReport() {
            if (this.downloadUrl) {
                window.open(this.downloadUrl, '_blank');
            }
            this.$emit('download');
        }
    }
};
</script>